export const LetterBoxType = {
  Vertical: 0,
  Horizontal: 1
} as const;

export type LetterBoxType = (typeof LetterBoxType)[keyof typeof LetterBoxType];

export class PointerCorrector {
  private _videoWidth!: number;
  private _videoHeight!: number;
  private _videoElem!: HTMLVideoElement;
  private _contentRect!: { x: number; y: number; width: number; height: number };

  /**
   * @param videoWidth {number}
   * @param videoHeight {number}
   * @param videoElem {HTMLVideoElement}
   */
  constructor(videoWidth: number, videoHeight: number, videoElem: HTMLVideoElement) {
    this.reset(videoWidth, videoHeight, videoElem);
  }

  /**
   * @param position {number[]} MouseEvent.clientX, MouseEvent.clientY
   * @returns {number[]}
   */
  map(position: number[]): number[] {
    const rect = this._videoElem.getBoundingClientRect();
    const _position = new Array<number>(2);

    // (1) set origin point to zero
    _position[0] = position[0] - rect.left;
    _position[1] = position[1] - rect.top;

    // (2) translate Unity coordinate system (reverse y-axis)
    _position[1] = rect.height - _position[1];

    // (3) add offset of letterbox
    _position[0] -= this._contentRect.x;
    _position[1] -= this._contentRect.y;

    // (4) mapping element rectangle to video rectangle
    _position[0] = (_position[0] / this._contentRect.width) * this._videoWidth;
    _position[1] = (_position[1] / this._contentRect.height) * this._videoHeight;

    return _position;
  }

  /**
   * @param videoWidth {number}
   */
  setVideoWidth(videoWidth: number): void {
    this._videoWidth = videoWidth;
    this._reset();
  }

  /**
   * @param videoHeight {number}
   */
  setVideoHeight(videoHeight: number): void {
    this._videoHeight = videoHeight;
    this._reset();
  }

  /**
   * @param videoElem {HTMLVideoElement}
   */
  setRect(videoElem: HTMLVideoElement): void {
    this._videoElem = videoElem;
    this._reset();
  }

  /**
   * @param videoWidth {number}
   * @param videoHeight {number}
   * @param videoElem {HTMLVideoElement}
   */
  reset(videoWidth: number, videoHeight: number, videoElem: HTMLVideoElement): void {
    this._videoWidth = videoWidth;
    this._videoHeight = videoHeight;
    this._videoElem = videoElem;
    this._reset();
  }

  get letterBoxType(): LetterBoxType {
    const videoRatio = this._videoHeight / this._videoWidth;
    const rect = this._videoElem.getBoundingClientRect();
    const rectRatio = rect.height / rect.width;
    return videoRatio > rectRatio ? LetterBoxType.Vertical : LetterBoxType.Horizontal;
  }

  get letterBoxSize(): number {
    const rect = this._videoElem.getBoundingClientRect();
    switch (this.letterBoxType) {
      case LetterBoxType.Horizontal: {
        const ratioWidth = rect.width / this._videoWidth;
        const height = this._videoHeight * ratioWidth;
        return (rect.height - height) * 0.5;
      }
      case LetterBoxType.Vertical: {
        const ratioHeight = rect.height / this._videoHeight;
        const width = this._videoWidth * ratioHeight;
        return (rect.width - width) * 0.5;
      }
      default:
        throw new Error('invalid status');
    }
  }

  /**
   * Returns rectangle for displaying video with the origin at the left-top of the element.
   * Not considered applying CSS like `object-fit`.
   * @returns {Object}
   */
  get contentRect(): { x: number; y: number; width: number; height: number } {
    const letterBoxType = this.letterBoxType;
    const letterBoxSize = this.letterBoxSize;

    const rect = this._videoElem.getBoundingClientRect();

    const x = letterBoxType === LetterBoxType.Vertical ? letterBoxSize : 0;
    const y = letterBoxType === LetterBoxType.Horizontal ? letterBoxSize : 0;
    const width = letterBoxType === LetterBoxType.Vertical ? rect.width - letterBoxSize * 2 : rect.width;
    const height = letterBoxType === LetterBoxType.Horizontal ? rect.height - letterBoxSize * 2 : rect.height;

    return { x, y, width, height };
  }

  private _reset(): void {
    this._contentRect = this.contentRect;
  }
}
